import React, { useEffect, useReducer, useMemo } from 'react';

import Spinner from 'components/shared/Spinner';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { orientationAndRatioPercentage } from 'utils/math_util';
import { maskImageLoadedReducer } from 'utils/reducers';

import { selectZoomedPPI } from 'selectors/ppi';
import { prepareArrangementMovement } from 'thunks/arrangement_thunks';
import { selectCanvasProductLoading } from 'selectors/loading_states';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';
import products from 'domains/products';
import ui from 'domains/ui';
import dimensions from 'domains/stage_dimensions';

import Node from './Node';
import CanvasTools from './CanvasTools';

import type { NextSizePayload } from 'domains/canvases';

type CanvasProps = {
  canvasId: string;
  isEditingCanvas: boolean;
  setEditingCanvasId: (canvasId: string | null) => void;
  setShouldCenterArrangement: (set: boolean) => void;
};

function Canvas(props: CanvasProps) {
  const { canvasId, isEditingCanvas, setEditingCanvasId, setShouldCenterArrangement } = props;

  const dispatch = useAppDispatch();
  const activeCanvasId = useAppSelector(activeEditor.selectors.canvasId);
  const canvasIds = useAppSelector(canvases.selectors.selectIds);
  const activeSubcatalog = useAppSelector(activeEditor.selectors.subcatalog);
  const wallPreviewEnabled = useAppSelector(dimensions.selectors.wallPreviewActive);
  const canvasConfig = useAppSelector((state) => canvases.selectors.selectById(state, canvasId));
  const canvasProduct = useAppSelector((state) =>
    products.selectors.selectById(state, canvasConfig?.product || '')
  );
  const visualOnlyRotation = useAppSelector(ui.selectors.visualRotation);
  const ppi = useAppSelector(selectZoomedPPI);
  const canvasProductLoading = useAppSelector(selectCanvasProductLoading);

  const nodes = useAppSelector((state) => canvases.selectors.nodesForCanvas(state, canvasId));

  // Mask node images that have been loaded
  const [maskImageLoaded, setMaskImageLoaded] = useReducer(maskImageLoadedReducer, {});

  const isCustomProduct = useMemo(() => {
    if (!canvasProduct) return false;

    return products.utils.isCustomSizeProduct(canvasProduct);
  }, [canvasProduct]);

  const canvasCustomSize = useMemo(() => {
    return {
      height: canvasConfig?.height || 0,
      width: canvasConfig?.width || 0,
    };
  }, [canvasConfig]);

  const productHeight = useMemo(() => {
    if (!canvasProduct || !canvasConfig) return 0;

    const { height } = products.utils.calculateProductSize(canvasProduct, canvasConfig);
    return height;
  }, [canvasProduct, canvasConfig]);

  const productWidth = useMemo(() => {
    if (!canvasProduct || !canvasConfig) return 0;

    const { width } = products.utils.calculateProductSize(canvasProduct, canvasConfig);
    return width;
  }, [canvasConfig, canvasProduct]);

  const canvasDimensions = useMemo(() => {
    let dimension = { height: 0, width: 0 };

    if (productHeight && productWidth) {
      dimension.height = productHeight * ppi;
      dimension.width = productWidth * ppi;
    }

    return dimension;
  }, [productWidth, productHeight, ppi]);

  const canvasOrientation = useMemo(() => {
    const { orientation } = orientationAndRatioPercentage(productWidth, productHeight);
    return orientation;
  }, [productWidth, productHeight]);

  const canvasRotation = useMemo(() => {
    return {
      rotation: canvasConfig?.rotation || 0,
      userRotated: canvasConfig?.userRotated || false,
    };
  }, [canvasConfig]);

  const isMultiNodeProduct = useMemo(() => {
    if (!canvasProduct) return false;

    return products.utils.productIsMultiNode(canvasProduct);
  }, [canvasProduct]);

  const canvasRotationAllowed = useMemo(() => {
    if (!canvasProduct) return false;

    return products.utils.canRotateProduct(canvasProduct);
  }, [canvasProduct]);

  const canvasPosition = useMemo(() => canvasConfig?.position, [canvasConfig]);

  const canvasStyle: React.CSSProperties = useMemo(() => {
    const x = canvasPosition?.x || 0;
    const y = canvasPosition?.y || 0;

    return {
      position: 'absolute',
      left: `${x * ppi}px`,
      top: `${y * ppi}px`,
      bottom: '0',
      right: '0',
      width: `${canvasDimensions.width}px`,
      height: `${canvasDimensions.height}px`,
      transform: `rotate(${visualOnlyRotation}deg)`,
      transition: 'transform 0.2s',
      cursor: 'default',
      zIndex: activeCanvasId === canvasId ? 2 : 1,
    };
  }, [activeCanvasId, canvasDimensions, canvasId, canvasPosition, ppi, visualOnlyRotation]);

  const maskNodesWithImage = useMemo(() => {
    return (nodes || [])
      .filter((node) => node.node_type === 2 && node.node_image !== '')
      .map((node) => node.node_image)
      .join(',');
  }, [nodes]);

  // Remove editing tools on regular stage if arrangements are disabled
  useEffect(() => {
    if (!activeSubcatalog?.arrangement && !wallPreviewEnabled) {
      setEditingCanvasId(null);
    }
  }, [activeSubcatalog, wallPreviewEnabled]);

  const canvasIsRendering = useMemo(() => {
    const maskImages = maskNodesWithImage.split(',');

    return (
      canvasProductLoading ||
      (maskNodesWithImage !== '' &&
        maskImages.length > 0 &&
        !maskImages.every((src) => maskImageLoaded[src]))
    );
  }, [canvasProductLoading, maskNodesWithImage, maskImageLoaded]);

  /*
   * Event Handlers
   */
  function enableDragCanvas() {
    setShouldCenterArrangement(false);
    const trackMovement = ({ movementX, movementY }: MouseEvent) => {
      dispatch(prepareArrangementMovement({ id: canvasId, movementX, movementY }));
    };

    document.addEventListener('mousemove', trackMovement);
    document.addEventListener(
      'mouseup',
      () => {
        document.removeEventListener('mousemove', trackMovement);
      },
      { once: true }
    );
  }

  function enableDragCanvasTouch(startEvent: TouchEvent) {
    setShouldCenterArrangement(false);
    let previousTouch: Touch | undefined;

    startEvent.preventDefault();

    const trackMovement = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      let movementX;
      let movementY;

      if (previousTouch) {
        movementX = touch.pageX - previousTouch.pageX;
        movementY = touch.pageY - previousTouch.pageY;
        previousTouch = touch;
      } else {
        previousTouch = touch;
        return;
      }

      dispatch(prepareArrangementMovement({ id: canvasId, movementX, movementY }));
    };

    document.addEventListener('touchmove', trackMovement, { passive: false });
    document.addEventListener(
      'touchend',
      () => {
        document.removeEventListener('touchmove', trackMovement);
        previousTouch = undefined;
      },
      { once: true }
    );
  }

  function handleProductResizeCanvasReposition(x: number, y: number, nextSize: NextSizePayload) {
    dispatch(
      prepareArrangementMovement({
        movementX: -x,
        movementY: -y,
        nextSize,
      })
    );
  }

  function handleCanvasClick(id: string) {
    if (activeCanvasId !== id) {
      dispatch(canvases.actions.setActiveCanvasId(id));
    }

    if (activeSubcatalog?.arrangement || wallPreviewEnabled) {
      setEditingCanvasId(id);
    }
  }

  return (
    // TODO: Any better way to handle this?
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`canvas${canvasIsRendering ? ' fade' : ''}`}
      data-testid={`canvas-${canvasId}`}
      style={canvasStyle}
      onClick={() => handleCanvasClick(canvasId)}
    >
      {canvasIsRendering && (
        <Spinner color="#fff" style={{ height: '50px', width: '50px', position: 'absolute' }} />
      )}
      {activeCanvasId === canvasId && canvasIds.length > 1 && <span className="active-indicator" />}
      {(nodes || []).map((node) => (
        <Node
          key={node.uid}
          node={node}
          canvasId={canvasId}
          canvasDimensions={canvasDimensions}
          canvasRotation={canvasRotation}
          canvasRotationAllowed={canvasRotationAllowed}
          canvasOrientation={canvasOrientation}
          isCustomProduct={isCustomProduct}
          isMultiNodeProduct={isMultiNodeProduct}
          setMaskImageLoaded={setMaskImageLoaded}
        />
      ))}
      {!canvasIsRendering && canvasProduct && isEditingCanvas && (
        <CanvasTools
          canvasId={canvasId}
          nodes={nodes || []}
          productWidth={productWidth}
          productHeight={productHeight}
          isCustomProduct={isCustomProduct}
          canvasRotation={canvasRotation}
          canvasCustomSize={canvasCustomSize}
          canvasDimensions={canvasDimensions}
          enableDragCanvas={enableDragCanvas}
          enableDragCanvasTouch={enableDragCanvasTouch}
          canvasProduct={canvasProduct}
          handleProductResizeCanvasReposition={handleProductResizeCanvasReposition}
        />
      )}
    </div>
  );
}

export default Canvas;
